import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { incidentsApi, patientApi, lmsApi } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import {
  AlertTriangle, Loader2, Camera, X, ArrowLeft, ShieldAlert,
} from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const SEVERITY_CFG = {
  low: { color: '#16A34A', bg: '#DCFCE7' },
  medium: { color: '#CA8A04', bg: '#FEF3C7' },
  high: { color: '#DC2626', bg: '#FEE2E2' },
  critical: { color: '#7F1D1D', bg: '#FEE2E2' },
};

export default function SubmitIncidentPage() {
  const navigate = useNavigate();
  const [types, setTypes] = useState([]);
  const [severities, setSeverities] = useState([]);
  const [patients, setPatients] = useState([]);
  const [selectedTypeKey, setSelectedTypeKey] = useState('');
  const [severity, setSeverity] = useState('medium');
  const [patientId, setPatientId] = useState('');
  const [occurredAt, setOccurredAt] = useState(() => new Date().toISOString().slice(0, 16));
  const [location, setLocation] = useState('');
  const [narrative, setNarrative] = useState('');
  const [extras, setExtras] = useState({});
  const [photos, setPhotos] = useState([]);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    Promise.all([
      incidentsApi.listTypes(),
      patientApi.list({ limit: 500 }).catch(() => ({ data: [] })),
    ]).then(([t, p]) => {
      setTypes(t.data.types);
      setSeverities(t.data.severities);
      const list = Array.isArray(p.data) ? p.data : (p.data.patients || []);
      setPatients(list);
    });
  }, []);

  const selectedType = types.find(t => t.key === selectedTypeKey);

  const onPickType = (key) => {
    const t = types.find(x => x.key === key);
    setSelectedTypeKey(key);
    setSeverity(t?.default_severity || 'medium');
    setExtras({});
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const { data } = await lmsApi.upload('images', file);
      setPhotos((p) => [...p, `${BACKEND}${data.url}`]);
      toast.success('Photo uploaded');
    } catch { toast.error('Upload failed'); }
    finally { setUploadingPhoto(false); e.target.value = ''; }
  };

  const submit = async () => {
    if (!selectedTypeKey || !narrative.trim()) {
      toast.error('Pick an incident type and describe what happened');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await incidentsApi.create({
        patient_id: patientId || null,
        incident_type: selectedTypeKey,
        severity,
        occurred_at: new Date(occurredAt).toISOString(),
        location,
        narrative,
        extras,
        photos,
      });
      toast.success('Incident reported — your team has been notified');
      navigate(`/incidents/${data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not submit incident');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="submit-incident-page">
      <Button variant="ghost" size="sm" onClick={() => navigate(-1)}><ArrowLeft className="w-4 h-4 mr-1" />Back</Button>
      <div>
        <h1 className="text-2xl font-bold text-[#0A2342] flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
          <ShieldAlert className="w-6 h-6 text-[#DC2626]" />Report a Patient Incident
        </h1>
        <p className="text-sm text-[#64748B] mt-1">Your care coordinator, the assigned nurse, and the practice admin will be notified immediately.</p>
      </div>

      <Card className="careiq-card">
        <CardContent className="p-5 space-y-5">
          {/* Type picker */}
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-[#64748B] mb-2">What happened?</div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2" data-testid="incident-type-grid">
              {types.map(t => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => onPickType(t.key)}
                  className={`p-3 rounded-lg border-2 text-left transition ${selectedTypeKey === t.key ? 'border-[#DC2626] bg-[#FEE2E2]' : 'border-[#E2E8F0] bg-white hover:border-[#0E7490]'}`}
                  data-testid={`incident-type-${t.key}`}
                >
                  <div className="font-semibold text-sm text-[#0A2342]">{t.label}</div>
                  <div className="text-[10px] text-[#64748B] mt-0.5">Default: {t.default_severity}</div>
                </button>
              ))}
            </div>
          </div>

          {selectedType && (
            <>
              {/* Severity */}
              <div>
                <div className="text-xs font-bold uppercase tracking-wider text-[#64748B] mb-2">Severity</div>
                <div className="flex gap-2 flex-wrap" data-testid="severity-picker">
                  {severities.map(s => {
                    const cfg = SEVERITY_CFG[s];
                    return (
                      <button
                        key={s}
                        type="button"
                        onClick={() => setSeverity(s)}
                        className={`px-4 py-2 rounded-lg text-sm font-semibold border-2 transition ${severity === s ? 'border-current' : 'border-transparent hover:border-[#E2E8F0]'}`}
                        style={{ color: cfg.color, backgroundColor: cfg.bg }}
                        data-testid={`severity-${s}`}
                      >
                        {s.toUpperCase()}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Patient */}
              <div>
                <div className="text-xs font-bold uppercase tracking-wider text-[#64748B] mb-2">Patient (optional)</div>
                <Select value={patientId || 'none'} onValueChange={(v) => setPatientId(v === 'none' ? '' : v)}>
                  <SelectTrigger data-testid="incident-patient-trigger"><SelectValue placeholder="Select patient" /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    <SelectItem value="none">No specific patient</SelectItem>
                    {patients.map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.first_name} {p.last_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* When + where */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <div className="text-xs font-bold uppercase tracking-wider text-[#64748B] mb-2">When did it happen?</div>
                  <Input type="datetime-local" value={occurredAt} onChange={(e) => setOccurredAt(e.target.value)} data-testid="incident-occurred-input" />
                </div>
                <div>
                  <div className="text-xs font-bold uppercase tracking-wider text-[#64748B] mb-2">Location</div>
                  <Input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="e.g. patient bedroom, kitchen, bathroom" data-testid="incident-location-input" />
                </div>
              </div>

              {/* Type-specific extra fields */}
              {selectedType.extra_fields?.length > 0 && (
                <div className="space-y-3 p-4 bg-[#F8FAFC] rounded-lg border border-[#E2E8F0]" data-testid="incident-extras">
                  <div className="text-xs font-bold uppercase tracking-wider text-[#0E7490]">{selectedType.label} — additional details</div>
                  {selectedType.extra_fields.map(f => (
                    <div key={f.key}>
                      <label className="text-xs text-[#64748B] block mb-1">{f.label}</label>
                      {f.type === 'select' ? (
                        <Select value={extras[f.key] || ''} onValueChange={(v) => setExtras({ ...extras, [f.key]: v })}>
                          <SelectTrigger data-testid={`extra-${f.key}-trigger`}><SelectValue placeholder="Select…" /></SelectTrigger>
                          <SelectContent>
                            {f.options.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Input value={extras[f.key] || ''} onChange={(e) => setExtras({ ...extras, [f.key]: e.target.value })} placeholder={f.placeholder} data-testid={`extra-${f.key}-input`} />
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Narrative */}
              <div>
                <div className="text-xs font-bold uppercase tracking-wider text-[#64748B] mb-2">Describe what happened</div>
                <Textarea rows={5} value={narrative} onChange={(e) => setNarrative(e.target.value)} placeholder="Describe the incident, what you observed, what you did, and the patient's current condition." data-testid="narrative-input" />
              </div>

              {/* Photos */}
              <div>
                <div className="text-xs font-bold uppercase tracking-wider text-[#64748B] mb-2">Photos (optional)</div>
                <div className="flex gap-2 flex-wrap items-start">
                  {photos.map((url, i) => (
                    <div key={i} className="relative">
                      <img src={url} alt="" className="w-20 h-20 object-cover rounded-lg border border-[#E2E8F0]" />
                      <button type="button" onClick={() => setPhotos(p => p.filter((_, j) => j !== i))} className="absolute -top-2 -right-2 bg-[#991B1B] text-white rounded-full p-0.5" data-testid={`remove-photo-${i}`}><X className="w-3 h-3" /></button>
                    </div>
                  ))}
                  <label className={`w-20 h-20 border-2 border-dashed rounded-lg flex items-center justify-center cursor-pointer ${uploadingPhoto ? 'border-[#0E7490] bg-[#0E7490]/10' : 'border-[#E2E8F0] hover:border-[#0E7490]'}`}>
                    {uploadingPhoto ? <Loader2 className="w-5 h-5 animate-spin text-[#0E7490]" /> : <Camera className="w-5 h-5 text-[#64748B]" />}
                    <input type="file" accept="image/*" capture="environment" className="hidden" onChange={uploadPhoto} disabled={uploadingPhoto} data-testid="photo-upload" />
                  </label>
                </div>
                <div className="text-[10px] text-[#94A3B8] mt-1">Tap to take a photo or upload an existing image.</div>
              </div>

              <div className="flex justify-end pt-3 border-t border-[#E2E8F0]">
                <Button onClick={submit} disabled={submitting} className={`bg-[#DC2626] hover:bg-[#991B1B] text-white px-6 ${severity === 'critical' || severity === 'high' ? 'animate-pulse' : ''}`} data-testid="submit-incident-btn">
                  {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  <AlertTriangle className="w-4 h-4 mr-2" />Submit incident report
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
